import React, { useState, useEffect, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';

import { Menu } from 'primereact/menu';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { useSuperAdminDashboardStore } from '../../stateManagers/stores/superAdminDashboardStore';
import { GlobalButton } from '../../components/GlobalComponents/GlobalButton';
import AddSubjectModal from './AddSubjectModal';
import CustomConfirmDialog from '../../components/CustomConfirmDialog';
import CourseChips from './parts/CourseChips';

interface Subject {
  subject_id: string;
  subject_name: string;
  created_at: string | null;
}

interface Course {
  course_id: string;
  name: string;
  title: string;
  status: string;
}

interface CourseSubjectsResponse {
  success: boolean;
  data: {
    course_id: string;
    course_name: string;
    course_title: string;
    subjects: Subject[];
  };
}

const Subjects: React.FC = () => {
  const toast = useRef<Toast>(null);
  const menuRefs = useRef<{ [key: string]: Menu | null }>({});
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [courseInfo, setCourseInfo] = useState<{
    course_name: string;
    course_title: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedCourseId, setSelectedCourseId] = useState<string>('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingSubject, setEditingSubject] = useState<{
    subject_id: string;
    subject_name: string;
    course_id: string;
  } | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{
    visible: boolean;
    subjectId: string;
    subjectName: string;
  }>({
    visible: false,
    subjectId: '',
    subjectName: ''
  });

  // Get dashboard data from store
  const { data: dashboardData, loading: dashboardLoading, fetchDashboardData } = useSuperAdminDashboardStore();

  useEffect(() => {
    // Fetch dashboard data if not already loaded
    if (!dashboardData) {
      fetchDashboardData();
    }
  }, [dashboardData, fetchDashboardData]);

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .p-menu {
        border-radius: 12px !important;
        border: 1px solid #e0e0e0 !important;
        box-shadow: 0 8px 24px rgba(0,0,0,0.12) !important;
        padding: 8px !important;
      }
      .p-menu .p-menuitem-link {
        border-radius: 8px !important;
        padding: 10px 12px !important;
        margin: 2px 0 !important;
        transition: all 0.2s ease !important;
      }
      .p-menu .p-menuitem-link:hover {
        background: #f5f5f5 !important;
      }
      .p-menu .p-menuitem-icon {
        color: #0f3d3e !important;
        font-size: 0.9rem !important;
      }
      .p-menu .p-menuitem-text {
        color: #1a1a1a !important;
        font-weight: 600 !important;
        font-size: 0.85rem !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    if (selectedCourseId) {
      fetchSubjectsByCourse(selectedCourseId);
    } else {
      setSubjects([]);
      setCourseInfo(null);
    }
  }, [selectedCourseId]);

  const fetchSubjectsByCourse = async (courseId: string) => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.get<CourseSubjectsResponse>(`/v1/subjects/by-course/${courseId}`);
      
      if (response.data.success) {
        setSubjects(response.data.data.subjects);
        setCourseInfo({
          course_name: response.data.data.course_name,
          course_title: response.data.data.course_title
        });
      } else {
        setError('Failed to load subjects');
      }
    } catch (err: any) {
      console.error('Failed to fetch subjects:', err);
      setError(err.response?.data?.error || 'Failed to load subjects');
    } finally {
      setLoading(false);
    }
  };

  const handleCourseSelect = (course: Course) => {
    setSelectedCourseId(course.course_id === selectedCourseId ? '' : course.course_id);
  };

  const handleDeleteSubject = async (subjectId: string, subjectName: string) => {
    setDeleteDialog({
      visible: true,
      subjectId,
      subjectName
    });
  };

  const confirmDelete = async () => {
    try {
      await apiClient.delete(`/v1/subjects/${deleteDialog.subjectId}`);
      
      toast.current?.show({
        severity: 'success',
        summary: 'Deleted',
        detail: 'Subject deleted successfully',
        life: 3000
      });
      
      if (selectedCourseId) {
        fetchSubjectsByCourse(selectedCourseId);
      }
    } catch (err: any) {
      console.error('Failed to delete subject:', err);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.response?.data?.error || 'Failed to delete subject',
        life: 4000
      });
    }
  };

  const handleEditSubject = (subject: Subject) => {
    setEditingSubject({
      subject_id: subject.subject_id,
      subject_name: subject.subject_name,
      course_id: selectedCourseId
    });
    setShowAddModal(true);
  };

  const getMenuItems = (subject: Subject) => [
    {
      label: 'Edit',
      icon: 'pi pi-pencil',
      command: () => handleEditSubject(subject)
    },
    {
      label: 'Delete',
      icon: 'pi pi-trash',
      command: () => handleDeleteSubject(subject.subject_id, subject.subject_name)
    }
  ];

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };



  // Show loading state if dashboard is still loading
  if (dashboardLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px',
        flexDirection: 'column',
        gap: '16px'
      }}>
        <ProgressSpinner 
          style={{ width: '50px', height: '50px' }} 
          strokeWidth="3"
        />
        <p style={{ color: '#0f3d3e', fontSize: '0.9rem' }}>Loading dashboard data...</p>
      </div>
    );
  }

  // Show error if dashboard data is not available
  if (!dashboardData) {
    return (
      <div style={{ padding: '24px' }}>
        <Message
          severity="warn"
          text="Unable to load dashboard data. Please refresh the page."
          style={{ fontSize: '0.9rem' }}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '0', margin: '0 auto', minHeight: '100vh' }}>
      <Toast ref={toast} />
      <CustomConfirmDialog
        visible={deleteDialog.visible}
        onHide={() => setDeleteDialog({ visible: false, subjectId: '', subjectName: '' })}
        onConfirm={confirmDelete}
        title="Delete Subject"
        message={`Are you sure you want to delete "${deleteDialog.subjectName}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        severity="danger"
        icon="pi pi-trash"
      />
      
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 50%, #0a2f2f 100%)',
        borderRadius: '20px',
        padding: '32px',
        marginBottom: '24px',
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: '0 10px 40px rgba(15, 61, 62, 0.3)'
      }}>
        <div style={{
          position: 'absolute',
          top: '-50px',
          right: '-50px',
          width: '200px',
          height: '200px',
          background: 'rgba(212, 241, 126, 0.1)',
          borderRadius: '50%',
          filter: 'blur(40px)'
        }} />
        
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: '16px'
          }}>
            <div>
              <div style={{
                display: 'inline-block',
                padding: '5px 12px',
                background: 'rgba(212, 241, 126, 0.2)',
                borderRadius: '20px',
                marginBottom: '10px',
                fontSize: '0.7rem',
                fontWeight: '600',
                letterSpacing: '0.5px',
                textTransform: 'uppercase'
              }}>
                Subject Management
              </div>
              <h1 style={{
                margin: '0 0 8px 0',
                fontSize: '1.7rem',
                fontWeight: '700',
                color: 'white',
                letterSpacing: '-0.5px'
              }}>
                Subjects
              </h1>
              <p style={{
                margin: '0',
                color: 'rgba(255,255,255,0.8)',
                fontSize: '0.9rem'
              }}>
                Manage subjects across all courses
              </p>
              <div style={{
                marginTop: '12px',
                display: 'flex',
                gap: '16px',
                fontSize: '0.85rem',
                color: 'rgba(255,255,255,0.7)'
              }}>
                <span>
                  <i className="pi pi-book" style={{ marginRight: '6px' }}></i>
                  {subjects.length} {selectedCourseId ? 'Subjects' : 'Select a course'}
                </span>
                {selectedCourseId && courseInfo && (
                  <span>
                    <i className="pi pi-filter" style={{ marginRight: '6px' }}></i>
                    {courseInfo.course_name}
                  </span>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
              <GlobalButton
                label="Add Subject"
                variant="success"
                icon="pi pi-plus"
                onClick={() => {
                  setEditingSubject(null);
                  setShowAddModal(true);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Course Chips Filter */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        border: '1px solid #f0f0f0'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '16px'
        }}>
          <i className="pi pi-filter" style={{ color: '#0f3d3e', fontSize: '1rem' }}></i>
          <h3 style={{
            margin: 0,
            fontSize: '0.95rem',
            fontWeight: '600',
            color: '#1a1a1a'
          }}>
            Select Course to View Subjects
          </h3>
          {selectedCourseId && (
            <button
              onClick={() => setSelectedCourseId('')}
              style={{
                marginLeft: 'auto',
                padding: '6px 12px',
                background: 'transparent',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                color: '#666',
                fontSize: '0.8rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f5f5f5';
                e.currentTarget.style.borderColor = '#0f3d3e';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.borderColor = '#e0e0e0';
              }}
            >
              <i className="pi pi-times" style={{ fontSize: '0.75rem' }}></i>
              Clear Selection
            </button>
          )}
        </div>
        <CourseChips
          selectedCourseId={selectedCourseId}
          onCourseSelect={handleCourseSelect}
          showSearch={false}
        />
      </div>

      {error && (
        <Message 
          severity="error" 
          text={error}
          style={{ marginBottom: '20px', fontSize: '0.9rem', fontWeight: '500' }}
        />
      )}

      {/* Subjects Display */}
      {!selectedCourseId ? (
        <div style={{
          padding: '80px 20px',
          textAlign: 'center',
          background: 'white',
          borderRadius: '16px',
          border: '2px dashed #e0e0e0'
        }}>
          <i className="pi pi-graduation-cap" style={{ fontSize: '3.5rem', color: '#ddd', marginBottom: '16px' }}></i>
          <h3 style={{ margin: '0 0 8px 0', color: '#666', fontSize: '1.1rem', fontWeight: '600' }}>
            Select a Course
          </h3>
          <p style={{ margin: '0', color: '#999', fontSize: '0.9rem' }}>
            Click on a course chip above to view its subjects
          </p>
        </div>
      ) : loading ? (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          background: 'white',
          borderRadius: '16px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <ProgressSpinner 
              style={{ width: '50px', height: '50px' }} 
              strokeWidth="3"
            />
            <p style={{
              marginTop: '16px',
              color: '#0f3d3e',
              fontSize: '0.95rem'
            }}>
              Loading subjects...
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Course Info Card */}
          {courseInfo && (
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '20px',
              marginBottom: '24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              border: '1px solid #f0f0f0',
              display: 'flex',
              alignItems: 'center',
              gap: '16px'
            }}>
              <div style={{
                width: '50px',
                height: '50px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <i className="pi pi-graduation-cap" style={{ color: '#d4f17e', fontSize: '1.3rem' }}></i>
              </div>
              <div>
                <h3 style={{
                  margin: '0 0 4px 0',
                  fontSize: '1.1rem',
                  fontWeight: '600',
                  color: '#1a1a1a'
                }}>
                  {courseInfo.course_name}
                </h3>
                <p style={{
                  margin: 0,
                  fontSize: '0.85rem',
                  color: '#666'
                }}>
                  {courseInfo.course_title}
                </p>
              </div>
              <div style={{
                marginLeft: 'auto',
                padding: '8px 16px',
                background: '#d4f17e',
                borderRadius: '20px',
                fontSize: '0.85rem',
                fontWeight: '600',
                color: '#0f3d3e'
              }}>
                {subjects.length} {subjects.length === 1 ? 'Subject' : 'Subjects'}
              </div>
            </div>
          )}

          {/* Subjects List */}
          {subjects.length === 0 ? (
            <div style={{
              padding: '80px 20px',
              textAlign: 'center',
              background: 'white',
              borderRadius: '16px',
              border: '2px dashed #e0e0e0'
            }}>
              <i className="pi pi-book" style={{ fontSize: '3rem', color: '#ddd', marginBottom: '16px' }}></i>
              <h3 style={{ margin: '0 0 8px 0', color: '#666', fontSize: '1.1rem', fontWeight: '600' }}>
                No subjects found
              </h3>
              <p style={{ margin: '0 0 20px 0', color: '#999', fontSize: '0.9rem' }}>
                This course has no subjects yet
              </p>
            </div>
          ) : (
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '24px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              border: '1px solid #f0f0f0'
            }}>
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '12px'
              }}>
                {subjects.map((subject) => (
                  <div
                    key={subject.subject_id}
                    style={{
                      padding: '14px 20px',
                      background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                      border: '2px solid #e0e0e0',
                      borderRadius: '16px',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      position: 'relative'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 16px rgba(15,61,62,0.12)';
                      e.currentTarget.style.borderColor = '#0f3d3e';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.borderColor = '#e0e0e0';
                    }}
                  >
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <span style={{
                        fontSize: '0.9rem',
                        fontWeight: '700',
                        color: '#d4f17e'
                      }}>
                        {subject.subject_name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div style={{ flex: 1 }}>
                      <h4 style={{
                        margin: '0 0 4px 0',
                        fontSize: '0.95rem',
                        fontWeight: '600',
                        color: '#1a1a1a'
                      }}>
                        {subject.subject_name}
                      </h4>
                      <p style={{
                        margin: 0,
                        fontSize: '0.75rem',
                        color: '#999'
                      }}>
                        Created: {formatDate(subject.created_at)}
                      </p>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        menuRefs.current[subject.subject_id]?.toggle(e);
                      }}
                      style={{
                        padding: '8px 12px',
                        background: '#f5f5f5',
                        border: 'none',
                        borderRadius: '8px',
                        color: '#0f3d3e',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#e0e0e0';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#f5f5f5';
                      }}
                    >
                      <i className="pi pi-ellipsis-v" style={{ fontSize: '0.85rem' }}></i>
                    </button>
                    <Menu
                      model={getMenuItems(subject)}
                      popup
                      ref={(el) => {
                        menuRefs.current[subject.subject_id] = el;
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Add/Edit Subject Modal */}
      <AddSubjectModal
        visible={showAddModal}
        courseId={selectedCourseId || undefined}
        editingSubject={editingSubject}
        onHide={() => {
          setShowAddModal(false);
          setEditingSubject(null);
        }}
        onSuccess={() => {
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: editingSubject ? 'Subject updated successfully!' : 'Subject created successfully!',
            life: 3000
          });
          if (selectedCourseId) {
            fetchSubjectsByCourse(selectedCourseId);
          }
          setShowAddModal(false);
          setEditingSubject(null);
        }}
      />
    </div>
  );
};

export default Subjects;